import React, { useCallback, useEffect, useMemo, useState } from "react";
import SidebarAdmin from "../../menu/SidebarAdmin";
import Topbar from "../../menu/Topbar";
import Api from "../../Services/Api";
import "./LiveSalesDashboard.css";

const money = new Intl.NumberFormat("pt-BR", { style: "currency", currency: "BRL" });
const number = new Intl.NumberFormat("pt-BR");
const BRASILIA_TIME_ZONE = "America/Sao_Paulo";
const toNumber = (value) => (Number.isFinite(Number(value)) ? Number(value) : 0);
const dateKey = (value = new Date()) => {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  const parts = new Intl.DateTimeFormat("en-US", {
    timeZone: BRASILIA_TIME_ZONE,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).formatToParts(date);
  const values = Object.fromEntries(
    parts.filter(({ type }) => type !== "literal").map(({ type, value }) => [type, value])
  );
  return `${values.year}-${values.month}-${values.day}`;
};
const getOrders = (payload) => Array.isArray(payload) ? payload : payload?.data || payload?.items || [];
const getValue = (order) => toNumber(order?.total_value ?? order?.valor_total_pedido ?? order?.purchase_amount);
const parseRaw = (raw) => {
  if (!raw) return {};
  if (typeof raw === "object") return raw;
  try { return JSON.parse(raw); } catch (_) { return {}; }
};
const getItems = (order) => {
  const raw = parseRaw(order?.raw_json);
  const details = raw?.det || raw?.pedido_venda_produto?.det || raw?.itens || [];
  return Array.isArray(details) ? details : [];
};
const getItemName = (item) => item?.produto?.descricao || item?.descricao || item?.nome || "Produto não informado";
const getItemQuantity = (item) => toNumber(item?.produto?.quantidade ?? item?.quantidade ?? item?.qtd ?? 1) || 1;
const formatChannel = (value) => {
  const clean = String(value || "").replace(/[_-]+/g, " ").replace(/\s+/g, " ").trim();
  if (!clean) return "Omie";
  return clean.toLowerCase().replace(/\b\w/g, (letter) => letter.toUpperCase());
};
const getChannel = (order) => {
  const raw = parseRaw(order?.raw_json);
  return formatChannel(order?.origem_pedido || raw?.cabecalho?.origem_pedido || raw?.Cabecalho?.origem_pedido || raw?.origem_pedido || raw?.canal_venda);
};
const getItemTotal = (item) => {
  const product = item?.produto || {};
  const total = toNumber(product?.valor_total ?? item?.valor_total ?? product?.valor_total_bruto ?? item?.valor_total_bruto);
  if (total) return total;
  return getItemQuantity(item) * toNumber(product?.valor_unitario ?? item?.valor_unitario ?? product?.preco_unitario ?? item?.preco_unitario);
};
const getOrderRegisteredAt = (order) => order?.created_at || order?.updated_at || null;
const getOrderHour = (order) => {
  const date = new Date(getOrderRegisteredAt(order));
  if (Number.isNaN(date.getTime())) return null;
  const hour = new Intl.DateTimeFormat("en-US", {
    timeZone: BRASILIA_TIME_ZONE,
    hour: "2-digit",
    hourCycle: "h23",
  }).format(date);
  const parsed = Number(hour);
  return Number.isInteger(parsed) && parsed >= 0 && parsed < 24 ? parsed : null;
};
const formatOrderTime = (order) => {
  const date = new Date(getOrderRegisteredAt(order));
  if (Number.isNaN(date.getTime())) return "Horário indisponível";
  return new Intl.DateTimeFormat("pt-BR", {
    timeZone: BRASILIA_TIME_ZONE,
    hour: "2-digit",
    minute: "2-digit",
  }).format(date);
};

export default function LiveSalesDashboard() {
  const [menuClose, setMenuClose] = useState(false);
  const [orders, setOrders] = useState([]);
  const [users, setUsers] = useState([]);
  const [updatedAt, setUpdatedAt] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);
  const [activeHour, setActiveHour] = useState(null);

  const loadSales = useCallback(async () => {
    try {
      setError("");
      const today = dateKey();
      const [ordersResponse, usersResponse] = await Promise.all([
        Api.get("/omie/orders", { params: { date_from: today, date_to: today, limit: 500 } }),
        Api.get("/usuarios"),
      ]);
      setOrders(getOrders(ordersResponse.data));
      setUsers(getOrders(usersResponse.data));
      setUpdatedAt(new Date());
    } catch (err) {
      console.error("Erro ao carregar vendas ao vivo:", err);
      setError("Não foi possível carregar as vendas da Omie agora.");
    } finally { setLoading(false); }
  }, []);

  useEffect(() => {
    loadSales();
    const timer = window.setInterval(loadSales, 60000);
    return () => window.clearInterval(timer);
  }, [loadSales]);

  const metrics = useMemo(() => {
    const hourly = Array(24).fill(0);
    const products = new Map();
    const channels = new Map();
    const licensedById = new Map(users.map((user) => [Number(user?.id), user]));
    const licensed = new Map();
    let total = 0, units = 0;
    orders.forEach((order) => {
      total += getValue(order);
      const hour = getOrderHour(order);
      if (Number.isInteger(hour) && hour >= 0 && hour < 24) hourly[hour] += getValue(order);
      const licensedId = Number(order?.licensed_id);
      if (licensedId) {
        const user = licensedById.get(licensedId);
        const current = licensed.get(licensedId) || { id: licensedId, name: user?.nome || user?.full_name || user?.name || "Licenciado não identificado", count: 0, totalValue: 0 };
        current.count += 1;
        current.totalValue += getValue(order);
        licensed.set(licensedId, current);
      }
      const channel = getChannel(order);
      channels.set(channel, (channels.get(channel) || 0) + 1);
      getItems(order).forEach((item) => {
        const name = getItemName(item), quantity = getItemQuantity(item);
        units += quantity;
        const current = products.get(name) || { name, quantity: 0, totalValue: 0 };
        current.quantity += quantity;
        current.totalValue += getItemTotal(item);
        products.set(name, current);
      });
    });
    return { total, units, hourly, ticket: orders.length ? total / orders.length : 0,
      products: [...products.values()].sort((a, b) => b.quantity - a.quantity).slice(0, 5),
      topProductsByValue: [...products.values()].sort((a, b) => b.totalValue - a.totalValue).slice(0, 5),
      recentOrders: [...orders].sort((a, b) => new Date(getOrderRegisteredAt(b)) - new Date(getOrderRegisteredAt(a))).slice(0, 10),
      topLicensed: [...licensed.values()].sort((a, b) => b.totalValue - a.totalValue).slice(0, 5),
      topChannels: [...channels.entries()].map(([name, count]) => ({ name, count })).sort((a, b) => b.count - a.count).slice(0, 5) };
  }, [orders, users]);

  const chartMax = Math.max(...metrics.hourly, 1);
  const chartBars = metrics.hourly.map((value, hour) => ({
    hour,
    value,
    height: Math.max(value ? 8 : 0, (value / chartMax) * 164),
  }));
  const activeBar = activeHour === null ? null : chartBars[activeHour];
  const updatedLabel = updatedAt ? new Intl.DateTimeFormat("pt-BR", { timeZone: BRASILIA_TIME_ZONE, dateStyle: "long", timeStyle: "medium" }).format(updatedAt) : "Carregando...";

  return <div className="app-container">
    <SidebarAdmin close={menuClose} />
    <main className="main-content live-sales-page">
      <Topbar page="Vendas ao vivo" onToggleMenu={() => setMenuClose((value) => !value)} />
      <section className="live-sales-hero">
        <div className="live-sales-heading"><span className="live-dot" /> <span>Dados Omie atualizados a cada minuto</span></div>
        <h1>Vendas de hoje ao vivo</h1>
        <div className="live-total-card"><small>Faturamento bruto de hoje</small><strong>{money.format(metrics.total)}</strong><span>{updatedLabel}</span></div>
      </section>
      {error ? <div className="live-sales-error">{error}</div> : null}
      <section className="live-metrics">
        <article><span>Pedidos realizados</span><strong>{number.format(orders.length)}</strong></article>
        <article><span>Unidades vendidas</span><strong>{number.format(metrics.units)}</strong></article>
        <article><span>Ticket médio</span><strong>{money.format(metrics.ticket)}</strong></article>
        <article><span>Status</span><strong className="live-status">{loading ? "Carregando" : "Ao vivo"}</strong></article>
      </section>
      <section className="live-content-grid">
        <article className="live-panel live-chart-panel"><div className="live-chart-heading"><div><h2>Pedidos registrados por hora</h2><p>Faturamento conforme o horário de entrada no sistema</p></div>{activeBar?.value ? <strong>{String(activeBar.hour).padStart(2, "0")}h · {money.format(activeBar.value)}</strong> : null}</div>
          <div className="live-chart-wrap"><div className="live-chart-scale"><span>{money.format(chartMax)}</span><span>{money.format(chartMax / 2)}</span><span>R$ 0</span></div><svg viewBox="0 0 720 220" role="img" aria-label="Faturamento de pedidos registrados por hora" onMouseLeave={() => setActiveHour(null)}>
            <path className="live-grid" d="M 18 22 H 710 M 18 104 H 710 M 18 186 H 710" />
            {chartBars.map((bar) => { const x = 22 + bar.hour * 29; const y = 186 - bar.height; return <g key={bar.hour} onMouseEnter={() => setActiveHour(bar.hour)}><title>{`${String(bar.hour).padStart(2, "0")}h: ${money.format(bar.value)}`}</title><rect className={`live-bar ${activeHour === bar.hour ? "is-active" : ""}`} x={x} y={y} width="20" height={bar.height} rx="5" /><rect className="live-bar-hitarea" x={x - 4} y="14" width="28" height="176" /></g>; })}
          </svg></div>
          <div className="live-chart-axis">{[0, 3, 6, 9, 12, 15, 18, 21, 23].map((hour) => <span key={hour}>{String(hour).padStart(2, "0")}h</span>)}</div>
        </article>
        <article className="live-panel"><h2>Produtos mais vendidos</h2>
          <ol className="live-products">{metrics.products.length ? metrics.products.map((product, index) => <li key={product.name}><b>{index + 1}</b><span>{product.name}<small>{number.format(product.quantity)} un. vendidas</small></span><strong>{money.format(product.totalValue)}</strong></li>) : <li className="live-empty">Nenhum produto vendido hoje.</li>}</ol>
        </article>
      </section>
      <section className="live-rankings-grid">
        <article className="live-panel">
          <h2>Top licenciados por valor</h2><p>Faturamento total do dia</p>
          {metrics.topLicensed.length ? <ol className="live-ranking-list">
            {metrics.topLicensed.map((licensed, index) => <li key={licensed.id}><b>{index + 1}</b><span>{licensed.name}<small>{number.format(licensed.count)} pedidos</small></span><strong>{money.format(licensed.totalValue)}</strong></li>)}
          </ol> : <div className="live-empty">Nenhuma venda vinculada hoje.</div>}
        </article>
        <article className="live-panel">
          <h2>Top produtos por valor</h2><p>Faturamento total do dia</p>
          {metrics.topProductsByValue.length ? <ol className="live-ranking-list">
            {metrics.topProductsByValue.map((product, index) => <li key={product.name}><b>{index + 1}</b><span>{product.name}<small>{number.format(product.quantity)} un. vendidas</small></span><strong>{money.format(product.totalValue)}</strong></li>)}
          </ol> : <div className="live-empty">Nenhum produto vendido hoje.</div>}
        </article>
        <article className="live-panel">
          <h2>Top canais de venda</h2><p>Quantidade de pedidos do dia</p>
          {metrics.topChannels.length ? <ol className="live-ranking-list">
            {metrics.topChannels.map((channel, index) => <li key={channel.name}><b>{index + 1}</b><span>{channel.name}<small>Canal de venda</small></span><strong>{number.format(channel.count)} pedidos</strong></li>)}
          </ol> : <div className="live-empty">Nenhum canal identificado hoje.</div>}
        </article>
      </section>
      <section className="live-panel live-orders-panel">
        <div className="live-orders-heading"><div><h2>Compras recentes</h2><p>Clientes que compraram hoje pela Omie.</p></div><span>{number.format(metrics.recentOrders.length)} pedidos exibidos</span></div>
        {metrics.recentOrders.length ? <div className="live-orders-table">
          <div className="live-order-row live-order-head"><span>Cliente</span><span>Horário</span><span>Canal</span><span>Licenciado</span><span>Valor da compra</span></div>
          {metrics.recentOrders.map((order) => <div className="live-order-row" key={order.id || order.omie_order_id || order.order_number}>
            <strong>{order.client_name || "Cliente não informado"}<small>Pedido #{order.order_number || order.omie_order_id || "-"}</small></strong><span className="live-order-time"><small>Registrado às</small>{formatOrderTime(order)}</span><span>{getChannel(order)}</span><span>{Number(order.licensed_id) ? (users.find((user) => Number(user?.id) === Number(order.licensed_id))?.nome || "Licenciado não identificado") : "Não vinculado"}</span><strong>{money.format(getValue(order))}</strong>
          </div>)}
        </div> : <div className="live-empty live-orders-empty">Nenhuma compra encontrada hoje.</div>}
      </section>
    </main>
  </div>;
}
